import { Command } from "commander";
import chalk from "chalk";
import readline from "readline";
import { planRepairs, executeRepairs, RepairAction } from "../repair/repairEngine.js";
import { generateDiagnosticBundle } from "../diagnostics/bundleGenerator.js";

function askQuestion(query: string): Promise<string> {
  const rl = readline.createInterface({
    input: process.stdin,
    output: process.stdout,
  });
  return new Promise((resolve) => {
    rl.question(query, (answer) => {
      rl.close();
      resolve(answer.trim().toLowerCase());
    });
  });
}

export const repairCommand = new Command("repair")
  .description("Automatically detect and fix common React Native configuration issues")
  .option("--dry-run", "Show what issues would be repaired without applying changes")
  .option("--json", "Output repair results in JSON format")
  .option("--yes", "Automatically execute all repairs without interactive confirmation prompts")
  .option("--verbose", "Show detailed execution stdout/stderr and command logs")
  .action(async (options: { dryRun?: boolean; json?: boolean; yes?: boolean; verbose?: boolean }) => {
    try {
      const cwd = process.cwd();

      // 1. Scan and plan repairs
      const planned = await planRepairs(cwd);

      if (planned.length === 0) {
        if (options.json) {
          console.log(JSON.stringify({ actions: [] }, null, 2));
        } else {
          console.log();
          console.log(chalk.green("✓ No issues detected. Your project is completely healthy!"));
          console.log();
        }
        process.exit(0);
        return;
      }

      // 2. Dry run mode
      if (options.dryRun) {
        if (options.json) {
          console.log(JSON.stringify({ planned: planned.map((p) => ({ id: p.id, title: p.title })) }, null, 2));
        } else {
          console.log();
          console.log(chalk.bold.underline("Planned Repairs"));
          console.log();
          for (const action of planned) {
            const type = action.safe ? chalk.green("(Safe)") : chalk.yellow("(Requires confirmation)");
            console.log(`  ✓ ${action.title} ${type}`);
            console.log(`    ${chalk.dim(action.description)}`);
          }
          console.log();
          console.log(chalk.yellow("No changes were made. (Dry run)"));
          console.log();
        }
        process.exit(0);
        return;
      }

      // 3. Select execution path: interactive vs automated
      let proceed = false;
      if (options.yes || options.json) {
        proceed = true;
      } else {
        console.log();
        console.log(chalk.bold.yellow("Detected Issues"));
        for (const action of planned) {
          const severity = action.safe ? chalk.dim("(Safe)") : chalk.yellow("(Needs review)");
          console.log(`  • ${chalk.bold(action.title)} ${severity}`);
          console.log(`    ${chalk.dim(action.description)}`);
        }
        console.log();

        const answer = await askQuestion("Proceed with repairs? (y/n): ");
        if (answer === "y" || answer === "yes") {
          proceed = true;
        }
      }

      if (!proceed) {
        console.log();
        console.log(chalk.red("✖ Repair aborted by user."));
        console.log();
        process.exit(0);
        return;
      }

      // 4. Execute repairs sequentially
      if (!options.json) {
        console.log();
        console.log(chalk.bold.underline("React Native Repair"));
        console.log();
        console.log(chalk.bold("Running Repairs"));
      }

      const executed = await executeRepairs(cwd, planned, undefined, !!options.verbose, !!options.json);

      const failCount = executed.filter((a) => !a.success).length;

      // 5. Output formats
      if (options.json) {
        const summary = executed.map((a) => ({
          id: a.id,
          success: a.success,
          details: a.details,
        }));
        console.log(JSON.stringify({ actions: summary }, null, 2));
      }

      if (failCount > 0) {
        try {
          const reportPath = await generateDiagnosticBundle("repair", new Error(`Repair failed for ${failCount} actions.`));
          if (!options.json) {
            console.log(chalk.yellow(`Diagnostic bundle generated: ${reportPath}`));
          }
        } catch {}
      }

      process.exit(failCount > 0 ? 1 : 0);
    } catch (err: any) {
      const errorObj = err instanceof Error ? err : new Error(String(err));
      let reportPath = "";
      try {
        reportPath = await generateDiagnosticBundle("repair", errorObj);
      } catch {}

      if (options.json) {
        console.log(JSON.stringify({ 
          error: errorObj.message,
          diagnosticBundle: reportPath 
        }, null, 2));
      } else {
        console.log();
        console.log(chalk.red(`✖ Repair failed: ${errorObj.message}`));
        if (reportPath) {
          console.log(chalk.yellow(`Diagnostic bundle generated: ${reportPath}`));
        }
        console.log();
      }
      process.exit(1);
    }
  });
